import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { Lock, MoveHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, SectionHead, Kicker } from "./Reveal";
import { BEFORE_AFTER, IMAGES, PROJECTS } from "../data/content";

const REAL_PROJECTS = [
  {
    id: "fachwerkhaus",
    image: "/images/referenzen/fachwerkhaus.jpg",
    category: "Denkmalpflege · Fachwerk",
    title: "Fachwerkfassade in Schwarz-Weiß",
    text: "Balkenwerk, Gefache und Fenster – klassisch abgesetzt, sauber ausgeführt.",
    alt: "Originalaufnahme einer Referenz von Manfred Berglar, Malermeister: saniertes Fachwerkhaus mit schwarz-weißer Fassade, grüner Tür und Bauerngarten",
  },
  {
    id: "giebel-wandbild",
    image: "/images/referenzen/giebel-wandbild.jpg",
    category: "Kirchen & Sakralbau",
    title: "Giebel mit Wandbild",
    text: "Fassadenfläche und Wandmotiv am Kirchengiebel – Präzision im Detail.",
    alt: "Originalaufnahme einer Referenz von Manfred Berglar, Malermeister: Kirchengiebel mit kunstvollem Wandbild",
  },
  {
    id: "einfamilienhaus",
    image: "/images/referenzen/einfamilienhaus.jpg",
    category: "Privatkunden",
    title: "Einfamilienhaus im Südsauerland",
    text: "Fassadenanstrich in hellem Weiß – klar, ruhig und wetterfest.",
    alt: "Originalaufnahme einer Referenz von Manfred Berglar, Malermeister: Einfamilienhaus mit frisch gestrichener weißer Fassade",
  },
  {
    id: "kirche-rosette",
    image: "/images/referenzen/kirche-rosette.jpg",
    category: "Kirchen & Sakralbau",
    title: "Kirchenfassade mit Rosette",
    text: "Heller Fassadenanstrich rund um Rosette und Portal.",
    alt: "Originalaufnahme einer Referenz von Manfred Berglar, Malermeister: Kirche mit weißer Fassade und Rosettenfenster",
  },
];

const SliderHandle = () => (
  <div className="relative h-full flex items-center">
    <div className="w-px h-full bg-white" />
    <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-0 w-11 h-11 rounded-full bg-gold text-white flex items-center justify-center shadow-lg">
      <MoveHorizontal size={18} />
    </div>
  </div>
);

const Stamp = () => (
  <span
    aria-hidden="true"
    className="inline-flex items-center justify-center border-2 border-gold text-gold font-sans text-[10px] font-extrabold uppercase tracking-[0.28em] px-3 py-1.5 rounded-sm -rotate-6 select-none transition-transform duration-500 group-hover:rotate-0"
  >
    Diskret
  </span>
);

const Redacted = ({ w }) => (
  <span aria-hidden="true" className={`inline-block h-3.5 ${w} bg-ink rounded-[3px] align-middle`} />
);

const ProjectCard = ({ project, delay }) => (
  <Reveal delay={delay}>
    <article
      data-testid={`project-card-${project.id}`}
      className="group h-full flex flex-col border border-line rounded-xl overflow-hidden bg-white transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-ink/25"
    >
      <div className="relative overflow-hidden aspect-[16/10]" aria-label="Projektaufnahme – aus Diskretion bewusst unkenntlich">
        <img
          src={project.image}
          alt={`${project.title} – Projektarbeit von Manfred Berglar, Malermeister (Foto aus Diskretion bewusst verschleiert)`}
          loading="lazy"
          className="w-full h-full object-cover blur-[6px] grayscale-[0.6] scale-110 transition-[filter,transform] duration-700 group-hover:blur-[4px]"
        />
        <div className="absolute inset-0 bg-ink/45" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="w-12 h-12 rounded-full border border-white/50 bg-ink/40 backdrop-blur-sm flex items-center justify-center">
            <Lock size={18} className="text-white/90" strokeWidth={1.75} />
          </span>
        </div>
        <p className="absolute top-4 left-5 font-mono text-[10px] uppercase tracking-[0.22em] font-bold text-white/85">
          {project.file}
        </p>
      </div>
      <div className="p-7 flex flex-col gap-3 border-t-2 border-gold grow">
        <div className="flex items-start justify-between gap-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-golddark">{project.category}</p>
          <Stamp />
        </div>
        <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight text-ink">{project.title}</h3>
        <p className="text-sm text-muted leading-relaxed flex flex-wrap items-center gap-x-2 gap-y-2">
          Auftraggeber: <Redacted w="w-24" /> <Redacted w="w-16" />
        </p>
        <p className="text-muted leading-relaxed text-sm">{project.text}</p>
        <p className="mt-auto pt-4 border-t border-line font-mono text-[10px] uppercase tracking-[0.16em] font-bold text-muted flex items-center gap-2">
          <Lock size={12} className="text-golddark" /> {project.scope}
        </p>
      </div>
    </article>
  </Reveal>
);

export const RealProjects = () => (
  <section className="py-24 md:py-32 bg-white border-b border-line" data-testid="real-projects-section">
    <div className="container-x">
      <SectionHead
        kicker="Originalaufnahmen"
        testId="real-projects-title"
        title={
          <>
            Echte Objekte. <span className="font-fraktur text-golddark">Echtes Handwerk.</span>
          </>
        }
        text="Vier Arbeiten aus unserer Region – fotografiert am fertigen Objekt. Aus Diskretion nennen wir Auftraggeber und Adressen nicht öffentlich."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {REAL_PROJECTS.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.08}>
            <figure data-testid={`real-project-${p.id}`} className="group h-full flex flex-col">
              <div className="relative overflow-hidden rounded-xl border border-line aspect-[4/5]">
                <img
                  src={p.image}
                  alt={p.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
                <span className="absolute top-4 left-4 bg-ink/80 backdrop-blur text-white font-mono text-[9px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full pointer-events-none">
                  Originalaufnahme
                </span>
              </div>
              <figcaption className="pt-5 flex flex-col gap-1.5">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-golddark">{p.category}</p>
                <p className="font-display text-lg font-semibold tracking-tight text-ink leading-snug">{p.title}</p>
                <p className="text-sm text-muted leading-relaxed">{p.text}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const References = () => (
  <section id="referenzen" className="py-24 md:py-32 bg-surface border-b border-line" data-testid="references-section">
    <div className="container-x">
      <SectionHead
        kicker="Referenzen"
        testId="references-title"
        title={
          <>
            Große Arbeit. <span className="font-fraktur text-golddark">Stille Diskretion.</span>
          </>
        }
        text="Wir arbeiten für Menschen und Institutionen, die Wert auf Diskretion legen – und genau deshalb mit uns arbeiten. Namen, Adressen und Auftraggeber nennen wir nicht öffentlich. Was Sie hier sehen: das Handwerk, nicht den Kunden."
      />
      <div className="grid md:grid-cols-3 gap-5">
        {PROJECTS.map((p, i) => (
          <ProjectCard key={p.id} project={p} delay={i * 0.08} />
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-14 md:mt-16 border border-line rounded-xl bg-white px-8 py-8 md:px-12 md:py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="references-discretion-note">
          <p className="font-display text-xl md:text-2xl font-medium tracking-tight text-ink max-w-2xl text-balance">
            Konkrete Referenzen nennen wir Ihnen gern –{" "}
            <span className="font-fraktur text-golddark">persönlich, nicht öffentlich.</span>
          </p>
          <Link to="/kontakt" data-testid="references-inquiry-link" className="btn-primary shrink-0">
            Diskrete Auskunft anfragen
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export const BeforeAfter = () => (
  <section className="bg-surface border-y border-line py-24 md:py-32" data-testid="before-after-section">
    <div className="container-x mb-10 relative">
      <Reveal>
        <Kicker>Vorher / Nachher</Kicker>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-3">
          <h3 className="font-display text-3xl md:text-5xl font-semibold tracking-tight text-ink">{BEFORE_AFTER.title}</h3>
          <p className="text-muted leading-relaxed max-w-md">{BEFORE_AFTER.text}</p>
        </div>
      </Reveal>
    </div>
    <Reveal delay={0.1}>
      <div className="relative border-y border-line" data-testid="before-after-slider">
        <ReactCompareSlider
          className="w-full h-[60vh] md:h-[70vh]"
          itemOne={
            <ReactCompareSliderImage
              src={IMAGES.before}
              alt="Vorher – beispielhafte Visualisierung einer Fassade vor der Sanierung"
              style={{ objectFit: "cover", filter: "saturate(0.6) contrast(0.94) brightness(0.92)" }}
            />
          }
          itemTwo={
            <ReactCompareSliderImage
              src={IMAGES.after}
              alt="Nachher – beispielhafte Visualisierung einer sanierten Fassade"
              style={{ objectFit: "cover" }}
            />
          }
          handle={<SliderHandle />}
        />
        <span className="absolute top-5 left-5 md:top-8 md:left-8 bg-ink/85 backdrop-blur text-white font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-3.5 py-2 rounded-full pointer-events-none z-10">
          Vorher
        </span>
        <span className="absolute top-5 right-5 md:top-8 md:right-8 bg-ink/85 backdrop-blur text-white border-l-2 border-herald font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-3.5 py-2 rounded-full pointer-events-none z-10">
          Nachher
        </span>
        <span data-testid="before-after-visualization-note" className="absolute bottom-4 right-4 md:bottom-6 md:right-8 bg-ink/70 backdrop-blur text-white/80 font-mono text-[9px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full pointer-events-none z-10">
          Beispielhafte Visualisierung
        </span>
      </div>
    </Reveal>
    <Reveal delay={0.15}>
      <div className="container-x mt-10 relative">
        <Link to="/referenzen" data-testid="before-after-more-link" className="link-cta">
          Alle Referenzen ansehen
        </Link>
      </div>
    </Reveal>
  </section>
);


export const DiscretionTeaser = () => (
  <section className="bg-white border-b border-line" data-testid="discretion-teaser">
    <div className="container-x py-14 md:py-16">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-center gap-8 md:gap-12">
          <span className="w-14 h-14 shrink-0 rounded-full border border-line bg-surface flex items-center justify-center" aria-hidden="true">
            <Lock size={22} className="text-golddark" strokeWidth={1.5} />
          </span>
          <div className="flex-1">
            <p className="kicker">Diskretion</p>
            <p className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-ink mt-3 text-balance max-w-3xl">
              Was wir gebaut haben, sehen Sie gern.{" "}
              <span className="font-fraktur text-golddark">Wer dahinter steht, bleibt vertraulich.</span>
            </p>
            <p className="text-muted leading-relaxed mt-3 max-w-2xl text-sm md:text-base">
              Viele unserer Auftraggeber – privat wie öffentlich – schätzen genau das. Namen und Details
              nennen wir nicht öffentlich, sondern persönlich.
            </p>
          </div>
          <Link to="/referenzen" data-testid="discretion-teaser-link" className="link-cta shrink-0">
            Unser Archiv ansehen
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
